import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { IconAi, IconCalendar, IconCheck, IconClients, IconClose, IconDelete, IconDownload, IconEdit, IconExpenses, IconFilter, IconProjects, IconStar } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';
import MonthNavigator from '../components/MonthNavigator';
import EmptyState from '../components/EmptyState';
import VisibilityModal from '../components/VisibilityModal';
import { isDemoMode, redirectToSignup } from '../lib/demoMode';
import { usePlan } from '../lib/PlanContext';
import { DEMO_PROJECTS, DEMO_WORK_DATES, DEMO_CLIENTS } from '../lib/demoData';

const STAGES = ['提案中', '受注待ち', '受注', '対応中', '請求済み', '完了'];
// ステージ色・仕事タイプ色はテーマトークンから生成（light/dark 両対応）
const stageColors = (t) => ({
  '提案中':   { dot: t.textMuted,   badge: { bg: t.badgeBg1, color: t.badgeTxt1 }, bar: t.textMuted },   // 旧「商談中」の色を踏襲
  '受注待ち': { dot: t.colorBlue,   badge: { bg: t.badgeBg2, color: t.badgeTxt2 }, bar: t.colorBlue },   // 旧「受注」の色を踏襲
  '受注':     { dot: t.badgeTxt6,   badge: { bg: t.badgeBg6, color: t.badgeTxt6 }, bar: t.badgeTxt6 },   // 新規ステータス
  '対応中':   { dot: t.colorYellow, badge: { bg: t.badgeBg3, color: t.badgeTxt3 }, bar: t.colorYellow },  // 旧「進行中」の色を踏襲
  '請求済み': { dot: t.colorPurple, badge: { bg: t.badgeBg4, color: t.badgeTxt4 }, bar: t.colorPurple },  // 変更なし
  '完了':     { dot: t.colorGreen2, badge: { bg: t.badgeBg5, color: t.badgeTxt5 }, bar: t.colorGreen2 },  // 変更なし
});
const workTypes = (t) => [
  { value:'project', label:'📦 納品案件', color:{ bg:t.badgeBg1, text:t.badgeTxt1 } },
  { value:'hourly',  label:'⏱ 時給',     color:{ bg:t.badgeBg2, text:t.badgeTxt2 } },
  { value:'daily',   label:'📅 単発',     color:{ bg:t.badgeBg4, text:t.badgeTxt4 } },
  { value:'monthly', label:'🔁 月契約',   color:{ bg:t.badgeBg5, text:t.badgeTxt5 } },
];

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '未設定';
  return '\u00a5' + Number(amount).toLocaleString('ja-JP');
};
const formatDate = (dateStr) => {
  if (!dateStr) return '未設定';
  const d = new Date(dateStr);
  return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}`;
};

// 稼働日カレンダー選択コンポーネント（daily タイプ用）
function WorkDatePicker({ workDates, setWorkDates }) {
  const now = new Date();
  const { theme } = useTheme();
  const [pickerYear, setPickerYear] = React.useState(now.getFullYear());
  const [pickerMonth, setPickerMonth] = React.useState(now.getMonth() + 1);
  const daysInMonth = new Date(pickerYear, pickerMonth, 0).getDate();
  const firstDow = new Date(pickerYear, pickerMonth - 1, 1).getDay();
  const toggleDay = (day) => {
    const dateStr = `${pickerYear}-${String(pickerMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    setWorkDates(prev =>
      prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr].sort()
    );
  };
  return (
    <div style={{ background:theme.bgInput, borderRadius:10, padding:'12px', border:`1px solid ${theme.border}` }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <button onClick={() => { if(pickerMonth===1){setPickerMonth(12);setPickerYear(y=>y-1);}else setPickerMonth(m=>m-1); }}
          style={{ background:'none', border:'none', color:theme.textSecondary, fontSize:16, cursor:'pointer' }}>‹</button>
        <span style={{ fontSize:13, fontWeight:600, color:theme.textPrimary }}>{pickerYear}年{pickerMonth}月</span>
        <button onClick={() => { if(pickerMonth===12){setPickerMonth(1);setPickerYear(y=>y+1);}else setPickerMonth(m=>m+1); }}
          style={{ background:'none', border:'none', color:theme.textSecondary, fontSize:16, cursor:'pointer' }}>›</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, textAlign:'center' }}>
        {['日','月','火','水','木','金','土'].map(d => (
          <div key={d} style={{ fontSize:9, color: d==='日'?'#f87171':d==='土'?'#93c5fd':'#64748b', padding:'2px 0' }}>{d}</div>
        ))}
        {Array.from({length: firstDow}).map((_,i) => <div key={`e${i}`} />)}
        {Array.from({length: daysInMonth}).map((_,i) => {
          const day = i + 1;
          const dateStr = `${pickerYear}-${String(pickerMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const selected = workDates.includes(dateStr);
          const dow = (firstDow + i) % 7;
          return (
            <button key={day} onClick={() => toggleDay(day)}
              style={{ aspectRatio:'1', borderRadius:6, border:'none', fontSize:11, cursor:'pointer',
                background: selected ? '#7c3aed' : 'transparent',
                color: selected ? '#fff' : dow===0 ? '#f87171' : dow===6 ? '#93c5fd' : '#94a3b8',
                fontWeight: selected ? 700 : 400 }}>
              {day}
            </button>
          );
        })}
      </div>
      {workDates.length > 0 && (
        <div style={{ marginTop:10, display:'flex', flexWrap:'wrap', gap:4 }}>
          {workDates.map(d => {
            const dt = new Date(d+'T00:00:00');
            const dow = ['日','月','火','水','木','金','土'][dt.getDay()];
            return (
              <span key={d} style={{ fontSize:11, background:theme.badgeBg4, color:theme.badgeTxt4, padding:'2px 6px', borderRadius:6 }}>
                {d.slice(5).replace('-','/')}({dow})
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Projects() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { canAddProject, handlePlanError } = usePlan();   // 課金：案件数の上限
  const STAGE_COLOR = stageColors(theme);
  const WORK_TYPES = workTypes(theme);
  const now = new Date();
  const [selYear, setSelYear]   = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [showAll, setShowAll]   = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectExpenses, setProjectExpenses] = useState({}); // {project_id: [{...}]}
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [filterStage, setFilterStage] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('filter') || 'すべて';
  });
  const [filterUninvoiced, setFilterUninvoiced] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('filter') === 'uninvoiced';
  });

  const [filterClient, setFilterClient] = useState('すべて');
  const [form, setForm] = useState({ title:'', status:'提案中', work_type:'project', amount:'', hourly_rate:'', hours_worked:'', work_days:'', start_date:'', due_date:'', tag:'', memo:'', client_id:'', payment_method:'銀行振込' });
  const [estimateForm, setEstimateForm] = useState({ description:'', workload:'', role:'', price_range:'' });
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState('');
  const [showEstimate, setShowEstimate] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ date: '', amount: '', category: '交通費', memo: '', project_id: '', project_title: '' });
  const [expenseSaving, setExpenseSaving] = useState(false);
  const [expenseReceiptFile, setExpenseReceiptFile] = useState(null);
  const [expenseReceiptPreview, setExpenseReceiptPreview] = useState(null);
  const [expenseTaxIncluded, setExpenseTaxIncluded] = useState(false);
  const [workDates, setWorkDates] = useState([]); // daily用稼働日
  const [visibilityTarget, setVisibilityTarget] = useState(null);
  const [visibilityMap, setVisibilityMap] = useState({});
  const [adminInfoMap, setAdminInfoMap] = useState({}); // { admin_freelancer_id: { name, company_name } }
  const [hasAdmins, setHasAdmins] = useState(false);
  const [requestMap, setRequestMap] = useState({});
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => { loadAll(); }, []);

  // セッションは1回だけ解決して全fetchに渡す（getUser()の多重ネットワーク往復を解消）
  const resolveUser = async (passedUser) =>
    passedUser || (await supabase.auth.getSession()).data?.session?.user || null;

  const loadAll = async () => {
    if (isDemoMode()) { fetchProjects(); return; }
    const user = await resolveUser();
    if (!user) { setLoading(false); return; }
    await Promise.all([
      fetchProjects(user),
      fetchClients(user),
      fetchExpenseSummary(user),
      fetchVisibilityData(user),
      fetchRequestMap(user),
    ]);
  };

  const fetchVisibilityData = async (passedUser) => {
    if (isDemoMode()) return;
    try {
      const user = await resolveUser(passedUser);
      if (!user) return;
      const { data: rels, error: relsError } = await supabase
        .from('admin_freelancers')
        .select('id, admin_id')
        .eq('freelancer_id', user.id)
        .eq('status', 'active');
      if (relsError) { console.error('fetchVisibilityData rels error:', relsError); return; }
      if (!rels || rels.length === 0) { setHasAdmins(false); return; }
      setHasAdmins(true);
      const relIds = rels.map(r => r.id);

      // 管理者のprofile情報（会社名+氏名）を取得
      const adminIds = rels.map(r => r.admin_id);
      // profiles と visibility は両方 rels にしか依存しないため並列取得
      const [{ data: adminProfiles }, { data: vis, error: visError }] = await Promise.all([
        supabase.from('profiles').select('id, name, company_name').in('id', adminIds),
        supabase.from('admin_project_visibility').select('project_id, admin_freelancer_id').in('admin_freelancer_id', relIds),
      ]);
      // admin_freelancer_id → { name, company_name } のマップ
      const infoMap = {};
      rels.forEach(r => {
        const prof = (adminProfiles || []).find(p => p.id === r.admin_id);
        if (prof) infoMap[r.id] = { name: prof.name || '', company_name: prof.company_name || '' };
      });
      setAdminInfoMap(infoMap);

      if (visError) { console.error('fetchVisibilityData vis error:', visError); return; }
      // project_id → [{ admin_freelancer_id, adminName }] のマップ
      const map = {};
      (vis || []).forEach(v => {
        if (!map[v.project_id]) map[v.project_id] = [];
        map[v.project_id].push(v.admin_freelancer_id);
      });
      setVisibilityMap(map);
    } catch (e) { console.error('fetchVisibilityData catch:', e); }
  };

  // 管理者から受けた案件の依頼IDマップを取得
  const fetchRequestMap = async (passedUser) => {
    if (isDemoMode()) return;
    try {
      const user = await resolveUser(passedUser);
      if (!user) return;
      const { data, error } = await supabase
        .from('project_requests')
        .select('id, title, status')
        .eq('freelancer_id', user.id)
        .in('status', ['accepted', 'withdraw_requested']);
      if (error) { console.error('fetchRequestMap error:', error); return; }
      if (!data || data.length === 0) return;
      const map = {};
      data.forEach(r => { map[r.id] = { requestId: r.id, title: r.title, status: r.status }; });
      setRequestMap(map);
    } catch(e) { console.error('fetchRequestMap catch:', e); }
  };

  // 取り下げ依頼を送信
  const handleWithdrawRequest = async () => {
    if (!withdrawTarget || withdrawing) return;
    setWithdrawing(true);
    try {
      const { error } = await supabase
        .from('project_requests')
        .update({ status: 'withdraw_requested' })
        .eq('id', withdrawTarget.requestId);
      if (error) throw error;
      // requestMapを更新
      setRequestMap(prev => ({
        ...prev,
        [withdrawTarget.requestId]: { ...prev[withdrawTarget.requestId], status: 'withdraw_requested' },
      }));
      setWithdrawTarget(null);
    } catch(e) {
      console.error(e);
      alert('送信に失敗しました');
    } finally {
      setWithdrawing(false);
    }
  };

  const fetchClients = async (passedUser) => {
    if (isDemoMode()) return;
    try {
      const user = await resolveUser(passedUser);
      if (!user) return;
      const { data } = await supabase.from('clients').select('id, name').eq('user_id', user.id).order('name');
      setClients(data || []);
    } catch(e) { console.error(e); }
  };

  const fetchExpenseSummary = async (passedUser) => {
    if (isDemoMode()) return;
    try {
      const user = await resolveUser(passedUser);
      if (!user) return;
      const { data } = await supabase.from('expenses').select('id, amount, project_id').eq('user_id', user.id).not('project_id', 'is', null);
      if (!data) return;
      const map = {};
      data.forEach(e => {
        if (!map[e.project_id]) map[e.project_id] = [];
        map[e.project_id].push(e);
      });
      setProjectExpenses(map);
    } catch(e) { console.error(e); }
  };

  const fetchProjects = async (passedUser) => {
    setLoading(true);
    try {
      // ── デモモード ──────────────────────────────────────────
      if (isDemoMode()) {
        setProjects(DEMO_PROJECTS);
        setWorkDates(DEMO_WORK_DATES);
        setClients(DEMO_CLIENTS);
        setLoading(false);
        return;
      }
      // ── 通常モード ──────────────────────────────────────────
      const user = await resolveUser(passedUser);
      if (!user) { setLoading(false); return; }
      const { data, error } = await supabase
        .from('projects').select('*, clients(id, name), project_work_dates(date)')
        .eq('user_id', user.id).order('created_at', { ascending: false });
      if (!error) setProjects(data || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const openNew = () => {
    if (!canAddProject(projects.length)) return;   // 課金：上限に達していたらモーダルを出して開かない
    setEditProject(null);
    setForm({ title:'', status:'提案中', work_type:'project', amount:'', hourly_rate:'', hours_worked:'', work_days:'', start_date:'', due_date:'', tag:'', memo:'', client_id:'', payment_method:'銀行振込' }); setWorkDates([]);
    setEstimate(null); setEstimateError(''); setShowEstimate(false);
    setEstimateForm({ description:'', workload:'', role:'', price_range:'' });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditProject(p);
    setForm({ title:p.title, status:p.status, work_type:p.work_type||'project', amount:p.amount||'', hourly_rate:p.hourly_rate||'', hours_worked:p.hours_worked||'', work_days:p.work_days||'', start_date:p.start_date||'', due_date:p.due_date||'', tag:p.tag||'', memo:p.memo||'', client_id:p.client_id||'', payment_method:p.payment_method||'銀行振込' });
    const existingDates = (p.project_work_dates || []).map(w => w.date);
    setWorkDates(existingDates);
    setEstimate(null); setEstimateError(''); setShowEstimate(false);
    setEstimateForm({ description:'', workload:'', role:'', price_range:'' });
    setShowModal(true);
  };

  const handleEstimate = async () => {
    if (!form.title.trim()) return setEstimateError('案件名を先に入力してください');
    setEstimateLoading(true);
    setEstimateError('');
    setEstimate(null);
    if (isDemoMode()) { redirectToSignup(); return; }
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id;
      const res = await fetch('/api/estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-Id': userId } : {}) },
        body: JSON.stringify({
          title: form.title,
          description: estimateForm.description,
          due_date: form.due_date,
          workload: estimateForm.workload,
          role: estimateForm.role,
          price_range: estimateForm.price_range,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setEstimateError(data.message || data.error || 'エラーが発生しました'); return; }
      setEstimate(data);
      // estimatesテーブルに保存
      if (userId) {
        await supabase.from('estimates').insert([{
          user_id: userId,
          project_id: editProject?.id || null,
          payload_json: data,
        }]);
      }
    } catch (e) {
      setEstimateError('通信エラーが発生しました');
    } finally {
      setEstimateLoading(false);
    }
  };

  const applyEstimate = () => {
    if (!estimate) return;
    setForm(f => ({ ...f, amount: String(estimate.suggested_price_yen) }));
    setEstimate(null);
    setShowEstimate(false);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    if (isDemoMode()) { redirectToSignup(); return; }
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) { alert('ログインが必要です'); return; }
      const payload = {
        title:form.title, status:form.status,
        work_type: form.work_type || 'project',
        amount: (() => {
          if (form.work_type === 'hourly') return (form.hourly_rate && form.hours_worked && workDates.length > 0) ? parseInt(form.hourly_rate)*parseFloat(form.hours_worked)*workDates.length : null;
          if (form.work_type === 'daily') return form.hourly_rate ? parseInt(form.hourly_rate) : null;
          if (form.work_type === 'monthly') return form.amount ? parseInt(form.amount) : null;
          return form.amount ? parseInt(form.amount) : null;
        })(),
        hourly_rate: form.hourly_rate ? parseInt(form.hourly_rate) : null,
        hours_worked: form.hours_worked ? parseFloat(form.hours_worked) : null,
        work_days: (form.work_type === 'daily' || form.work_type === 'hourly') ? workDates.length : (form.work_days ? parseInt(form.work_days) : null),
        start_date:form.start_date||null,
        due_date:form.due_date||null, tag:form.tag, memo:form.memo,
        user_id:user.id,
        client_id: form.client_id || null,
        payment_method: form.payment_method || '銀行振込',
      };
      let projectId;
      if (editProject) {
        const { error } = await supabase.from('projects').update(payload).eq('id', editProject.id);
        if (error) { console.error('update error:', error); alert('更新に失敗しました: ' + error.message); return; }
        projectId = editProject.id;
      } else {
        const { data: inserted, error } = await supabase.from('projects').insert([payload]).select('id').single();
        if (error) {
          if (handlePlanError(error)) { setShowModal(false); return; }   // 課金：DBトリガーが弾いた場合
          console.error('insert error:', error); alert('追加に失敗しました: ' + error.message); return;
        }
        projectId = inserted?.id;
      }
      if ((form.work_type === 'daily' || form.work_type === 'hourly') && projectId) {
        await supabase.from('project_work_dates').delete().eq('project_id', projectId);
        if (workDates.length > 0) {
          const rows = workDates.map(date => ({ project_id: projectId, user_id: user.id, date }));
          await supabase.from('project_work_dates').insert(rows);
        }
      }
      setShowModal(false);
      fetchProjects();
    } catch(e) { console.error(e); alert('エラーが発生しました: ' + e.message); }
  };

  const handleDelete = async (id) => {
    if (isDemoMode()) { redirectToSignup(); return; }
    if (!window.confirm('この案件を削除しますか？')) return;
    await supabase.from('projects').delete().eq('id', id);
    fetchProjects();
  };

  const handleDuplicate = async (p) => {
    if (isDemoMode()) { redirectToSignup(); return; }
    if (!canAddProject(projects.length)) return;   // 課金：複製も1件追加なので上限チェック
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;
      const { error } = await supabase.from('projects').insert([{
        title: p.title + '（コピー）',
        status: '提案中',
        amount: p.amount,
        due_date: p.due_date,
        tag: p.tag,
        memo: p.memo,
        client_id: p.client_id,
        user_id: user.id,
      }]);
      if (error) {
        if (handlePlanError(error)) return;
        console.error('duplicate error:', error); alert('複製に失敗しました: ' + error.message); return;
      }
      fetchProjects();
    } catch(e) { console.error(e); }
  };

  const handleAddSample = async () => {
    if (isDemoMode()) { redirectToSignup(); return; }
    if (!canAddProject(projects.length)) return;   // 課金：上限チェック
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    if (!user) return;
    const today = new Date();
    const due = new Date(today);
    due.setDate(due.getDate() + 30);
    const { error } = await supabase.from('projects').insert([{
      title: 'サンプル案件（Webサイト制作）',
      status: '対応中',
      work_type: 'project',
      amount: 300000,
      start_date: today.toISOString().split('T')[0],
      due_date: due.toISOString().split('T')[0],
      memo: 'これはサンプル案件です。実際の案件に合わせて編集してください。',
      tag: 'サンプル',
      payment_method: '銀行振込',
      user_id: user.id,
    }]);
    if (error) {
      if (handlePlanError(error)) return;
      alert('サンプル追加エラー: ' + error.message); return;
    }
    fetchProjects();
  };

  const advanceStage = async (project) => {
    const idx = STAGES.indexOf(project.status);
    if (idx >= STAGES.length - 1) return;
    await supabase.from('projects').update({ status: STAGES[idx+1] }).eq('id', project.id);
    fetchProjects();
  };

  const openExpense = (p) => {
    setExpenseForm({
      date: new Date().toISOString().slice(0,10),
      amount: '', category: '交通費', memo: '',
      project_id: p.id, project_title: p.title,
    });
    setExpenseReceiptFile(null);
    setExpenseReceiptPreview(null);
    setExpenseTaxIncluded(false);
    setShowExpenseModal(true);
  };

  const saveExpense = async () => {
    if (!expenseForm.amount) return;
    if (isDemoMode()) { redirectToSignup(); return; }
    setExpenseSaving(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;

      // レシート画像アップロード
      let receipt_url = null;
      if (expenseReceiptFile) {
        const ext = expenseReceiptFile.name.split('.').pop().toLowerCase() || 'jpg';
        const path = `receipts/${user.id}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('expense-receipts')
          .upload(path, expenseReceiptFile, { upsert: true, contentType: expenseReceiptFile.type || 'image/jpeg' });
        if (!upErr) {
          const { data: urlData } = supabase.storage.from('expense-receipts').getPublicUrl(path);
          receipt_url = urlData?.publicUrl || null;
        }
      }

      await supabase.from('expenses').insert([{
        date: expenseForm.date,
        amount: parseInt(expenseForm.amount),
        category: expenseForm.category,
        memo: expenseForm.memo,
        project_id: expenseForm.project_id || null,
        user_id: user.id,
        receipt_url,
        tax_included: expenseTaxIncluded,
      }]);
      setShowExpenseModal(false);
      setExpenseReceiptFile(null);
      setExpenseReceiptPreview(null);
      fetchExpenseSummary();
    } catch(e) { console.error(e); }
    setExpenseSaving(false);
  };

  const EXPENSE_CATEGORIES = ['交通費','通信費','交際費','消耗品費','広告費','外注費','会議費','その他'];

  const todayStr = new Date().toISOString().split('T')[0];
  const monthStr = `${selYear}-${String(selMonth).padStart(2,'0')}`;
  const filtered = projects
    .filter(p => {
      if (showAll) return true;
      const wt = p.work_type || 'project';
      // 単発・時給：稼働日（project_work_dates）で判定
      if (wt === 'daily' || wt === 'hourly') {
        const wdates = (p.project_work_dates || []).map(w => w.date || '');
        if (wdates.length > 0) {
          // 稼働日が登録済み：その月に稼働日があるときだけ表示
          return wdates.some(d => d.startsWith(monthStr));
        }
        // 稼働日が未登録のみ created_at にフォールバック
        const ca = p.created_at ? p.created_at.slice(0, 7) : '';
        return ca === monthStr;
      }
      // 納品案件・月契約：start_date で判定
      const sd = p.start_date || '';
      if (sd) return sd.startsWith(monthStr);
      // start_date 未設定の場合は due_date にフォールバック
      const dd = p.due_date || '';
      if (dd) return dd.startsWith(monthStr);
      // 日付なし案件：created_at で判定
      const ca = p.created_at ? p.created_at.slice(0, 7) : '';
      return ca === monthStr;
    })
    .filter(p => filterUninvoiced
      ? (p.status !== '請求済み' && p.status !== 'invoiced' && (p.amount || 0) > 0)
      : (filterStage === 'すべて' || p.status === filterStage))
    .filter(p => filterClient === 'すべて' || p.client_id === filterClient);

  // 案件タイプごとの実際の合計金額を計算
  const calcProjectTotal = (p) => {
    switch (p.work_type) {
      case 'daily':   // 日当 × 稼働日数
        return p.hourly_rate && (p.project_work_dates||[]).length > 0
          ? p.hourly_rate * (p.project_work_dates||[]).length
          : (p.amount || 0);
      case 'hourly':  // 時給 × 時間 × 日数
        return p.hourly_rate && p.hours_worked && p.work_days
          ? p.hourly_rate * p.hours_worked * p.work_days
          : (p.amount || 0);
      case 'project': // 納品金額（固定）
      case 'monthly': // 月額（固定）
      default:
        return p.amount || 0;
    }
  };

  // 月単位の集計（タイプ別基準日で判定）
  const monthProjects = projects.filter(p => {
    const wt = p.work_type || 'project';
    if (wt === 'daily' || wt === 'hourly') {
      const wdates = (p.project_work_dates || []).map(w => w.date || '');
      return wdates.some(d => d.startsWith(monthStr));
    }
    const sd = p.start_date || '';
    if (sd) return sd.startsWith(monthStr);
    const dd = p.due_date || '';
    return dd.startsWith(monthStr);
  });
  const totalAmount    = monthProjects.filter(p => p.status !== '提案中' && p.status !== '受注待ち').reduce((s,p) => s + calcProjectTotal(p), 0);
  const activeCount    = monthProjects.filter(p => p.status === '対応中').length;
  const completedCount = monthProjects.filter(p => p.status === '完了').length;

  const s = {
    card: { background:theme.bgCard, border:`1px solid ${theme.border}`, boxShadow:theme.shadow, borderRadius:14, overflow:'hidden' },
    label: { fontSize:11, color:theme.textSecondary, marginBottom:4 },
    input: { width:'100%', background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:'10px 14px', fontSize:14, color:theme.textPrimary, outline:'none', boxSizing:'border-box' },
  };

  // ── カレンダービュー ──
  const CalendarView = () => {
    const [selectedDay, setSelectedDay] = useState(null);
    const firstDay = new Date(selYear, selMonth - 1, 1);
    const lastDay  = new Date(selYear, selMonth, 0);
    const startDow = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const mFirst = `${selYear}-${String(selMonth).padStart(2,'0')}-01`;
    const mLast  = `${selYear}-${String(selMonth).padStart(2,'0')}-${String(daysInMonth).padStart(2,'0')}`;

    // タイムゾーン問題を回避するため文字列比較で判定
    // 日付なし案件はcreated_atの日付でプロット
    const calProjects = projects.filter(p => {
      const sd = p.start_date || '';
      const dd = p.due_date || '';
      // 日付なし案件：project_work_dates に当月稼働日があれば表示
      if (!sd && !dd) {
        const wdates = (p.project_work_dates || []).map(w => w.date || '');
        if (wdates.some(d => d >= mFirst && d <= mLast)) return true;
        const ca = p.created_at ? p.created_at.slice(0, 10) : '';
        return ca >= mFirst && ca <= mLast;
      }
      const rangeStart = sd || dd;
      const rangeEnd   = dd || sd;
      return rangeStart <= mLast && rangeEnd >= mFirst;
    });

    // 日付ごとの案件マップ（文字列比較でタイムゾーン問題を回避）
    const dayProjects = {};
    calProjects.forEach(p => {
      const sd = p.start_date || '';
      const dd = p.due_date || '';

      let rangeStart, rangeEnd;
      if (!sd && !dd) {
        // 日付なし → created_atの日に1日だけプロット
        const ca = p.created_at ? p.created_at.slice(0, 10) : '';
        rangeStart = ca;
        rangeEnd   = ca;
      } else {
        rangeStart = sd || dd;
        rangeEnd   = dd || sd;
      }

      // clamp to month range
      const clampedStart = rangeStart < mFirst ? mFirst : rangeStart;
      const clampedEnd   = rangeEnd   > mLast  ? mLast  : rangeEnd;
      if (clampedStart > clampedEnd) return; // 範囲外はスキップ

      const startD = new Date(clampedStart + 'T00:00:00');
      const endD   = new Date(clampedEnd   + 'T00:00:00');
      for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
        const key = d.getDate();
        if (!dayProjects[key]) dayProjects[key] = [];
        if (!dayProjects[key].find(x => x.id === p.id)) dayProjects[key].push(p);
      }
    });

    const todayD = new Date();
    const isToday = (day) => todayD.getFullYear() === selYear && todayD.getMonth() + 1 === selMonth && todayD.getDate() === day;

    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const openNewOnDay = (day) => {
      if (!canAddProject(projects.length)) return;   // 課金：上限チェック
      const dateStr = `${selYear}-${String(selMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
      setEditProject(null);
      setForm({ title:'', status:'提案中', work_type:'project', amount:'', hourly_rate:'', hours_worked:'', work_days:'', start_date:dateStr, due_date:dateStr, tag:'', memo:'', client_id:'', payment_method:'銀行振込' }); setWorkDates([]);
      setEstimate(null); setEstimateError(''); setShowEstimate(false);
      setEstimateForm({ description:'', workload:'', role:'', price_range:'' });
      setShowModal(true);
      setSelectedDay(null);
    };

    return (
      <div>
        {/* 曜日ヘッダー */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:2 }}>
          {['日','月','火','水','木','金','土'].map((d,i) => (
            <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:600, padding:'6px 0',
              color: i===0 ? '#f87171' : i===6 ? '#93c5fd' : '#64748b' }}>{d}</div>
          ))}
        </div>

        {/* 日付マス */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3 }}>
          {cells.map((day, idx) => {
            const dow = idx % 7;
            const projs = day ? (dayProjects[day] || []) : [];
            const isSelected = selectedDay === day;
            return (
              <div key={idx}
                onClick={() => { if (day) setSelectedDay(isSelected ? null : day); }}
                style={{
                  minHeight: 88, background: day ? (isSelected ? theme.badgeBg14 : theme.bgCard) : 'transparent',
                  border: day ? (isToday(day) ? `1px solid ${theme.accent}` : isSelected ? `1px solid ${theme.accentHover}` : `1px solid ${theme.border}`) : 'none',
                  borderRadius: 8, padding: '6px 4px 6px 8px', overflow:'hidden',
                  cursor: day ? 'pointer' : 'default',
                }}>
                {day && (
                  <>
                    <div style={{ fontSize:12, fontWeight: isToday(day) ? 700 : 400,
                      color: isToday(day) ? '#a5b4fc' : dow===0 ? '#f87171' : dow===6 ? '#93c5fd' : '#94a3b8',
                      marginBottom:3 }}>{day}</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                      {projs.slice(0,2).map(p => {
                        const sc = STAGE_COLOR[p.status] || STAGE_COLOR['提案中'];
                        return (
                          <button key={p.id}
                            onClick={e => { e.stopPropagation(); openEdit(p); }}
                            style={{ background:sc.badge.bg, border:'none', borderRadius:3, padding:'2px 4px',
                              fontSize:10, color:sc.badge.color, cursor:'pointer', textAlign:'left',
                              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', width:'100%' }}>
                            {p.title}
                          </button>
                        );
                      })}
                      {projs.length > 2 && (
                        <div style={{ fontSize:9, color:theme.textWeak }}>+{projs.length - 2}件</div>
                      )}
                      {/* 選択時: ＋追加ボタン */}
                      {isSelected && (
                        <button
                          onClick={e => { e.stopPropagation(); openNewOnDay(day); }}
                          style={{ background:theme.accent, border:'none', borderRadius:3, padding:'2px 4px',
                            fontSize:10, color:'#fff', cursor:'pointer', width:'100%', marginTop:2 }}>
                          ＋ 追加
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* 凡例 */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:12 }}>
          {STAGES.map(st => {
            const sc = STAGE_COLOR[st];
            return (
              <span key={st} style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:sc.badge.bg, color:sc.badge.color }}>
                {st} {projects.filter(p=>p.status===st).length}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding:'20px 16px', maxWidth: viewMode === 'calendar' ? 900 : 680, margin:'0 auto' }}>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, color:theme.textPrimary, margin:0 }}><IconProjects size={18}/> 案件管理</h1>
          <p style={{ fontSize:13, color:theme.textMuted, margin:'4px 0 0' }}>{filtered.length}件表示中</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {/* ビュー切り替え */}
          <div style={{ display:'flex', background:theme.bgCard, border:`1px solid ${theme.border}`, boxShadow:theme.shadow, borderRadius:8, overflow:'hidden' }}>
            <button onClick={() => setViewMode('list')} style={{ padding:'7px 12px', border:'none', cursor:'pointer', fontSize:13,
              background: viewMode==='list' ? theme.accent : 'transparent', color: viewMode==='list' ? theme.accentText : theme.textMuted }}><IconFilter size={16}/></button>
            <button onClick={() => setViewMode('calendar')} style={{ padding:'7px 12px', border:'none', cursor:'pointer', fontSize:13,
              background: viewMode==='calendar' ? theme.accent : 'transparent', color: viewMode==='calendar' ? theme.accentText : theme.textMuted }}><IconCalendar size={16}/></button>
          </div>
          <button onClick={openNew} style={{ background:theme.primary, color:theme.primaryText, border:'none', borderRadius:10, padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            ＋ 新規案件
          </button>
        </div>
      </div>

      {/* 月ナビゲーター */}
      <div style={{ marginBottom:16 }}>
        <MonthNavigator
          year={selYear} month={selMonth}
          onChange={(y,m) => { setSelYear(y); setSelMonth(m); setShowAll(false); }}
          showAllToggle showAll={showAll}
          onToggleAll={() => setShowAll(v => !v)}
        />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:18 }}>
        {[
          { label:`${selMonth}月の受注総額`, value:formatCurrency(totalAmount), color:theme.colorGreen2 },
          { label:`${selMonth}月 対応中`, value:`${activeCount}件`, color:theme.colorYellow },
          { label:`${selMonth}月 完了`, value:`${completedCount}件`, color:theme.accent },
        ].map(item => (
          <div key={item.label} style={{ ...s.card, borderTop:`2px solid ${item.color}`, padding:'14px 12px' }}>
            <div style={s.label}>{item.label}</div>
            <div style={{ fontSize:18, fontWeight:700, color:theme.textPrimary }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* ステージフィルター */}
      <div style={{ display:'flex', gap:6, marginBottom:8, overflowX:'auto', padding:'4px 0 10px', WebkitOverflowScrolling:'touch' }}>
        {['すべて', ...STAGES].map(st => (
          <button key={st} onClick={() => { setFilterStage(st); setFilterUninvoiced(false); }} style={{
            flexShrink:0, padding:'7px 14px', borderRadius:20, fontSize:12, fontWeight:500, cursor:'pointer', border:'none',
            background: !filterUninvoiced && filterStage===st ? theme.accent : theme.bgCard,
            color: !filterUninvoiced && filterStage===st ? '#fff' : theme.textMuted,
            outline: !filterUninvoiced && filterStage===st ? 'none' : `1px solid ${theme.border}`,
          }}>
            {st}{st!=='すべて' && ` ${projects.filter(p=>p.status===st).length}`}
          </button>
        ))}
        {/* 未請求フィルター */}
        {(() => { const cnt = projects.filter(p => p.status !== '請求済み' && p.status !== 'invoiced' && (p.amount || 0) > 0).length; return cnt > 0 ? (
          <button onClick={() => { setFilterUninvoiced(true); setFilterStage('すべて'); }} style={{
            flexShrink:0, padding:'7px 14px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', border:'none',
            background: filterUninvoiced ? theme.badgeBg4 : theme.bgCard,
            color: filterUninvoiced ? theme.badgeTxt4 : theme.colorPurple,
            outline: filterUninvoiced ? 'none' : `1px solid ${theme.border}`,
          }}>
            <IconStar size={14}/> 未請求 {cnt}
          </button>
        ) : null; })()}
      </div>

      {/* クライアントフィルター */}
      {clients.length > 0 && (
        <div style={{ display:'flex', gap:6, marginBottom:16, overflowX:'auto', padding:'4px 0 10px', WebkitOverflowScrolling:'touch' }}>
          {[{ id:'すべて', name:'すべてのクライアント' }, ...clients].map(c => (
            <button key={c.id} onClick={() => setFilterClient(c.id)} style={{
              flexShrink:0, padding:'6px 14px', borderRadius:20, fontSize:11, fontWeight:500, cursor:'pointer', border:'none',
              background: filterClient===c.id ? theme.accentBg : theme.bgCard,
              color: filterClient===c.id ? theme.colorBlue : theme.textMuted,
              outline: filterClient===c.id ? 'none' : `1px solid ${theme.border}`,
            }}>
              <IconClients size={12}/> {c.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:'48px 0', color:theme.textMuted }}>読み込み中...</div>
      ) : viewMode === 'calendar' ? (
        <CalendarView />
      ) : filtered.length === 0 ? (
        projects.length === 0 ? (
          <div>
            <EmptyState preset="projects" onAction={openNew} />
            <div style={{ textAlign:'center', paddingBottom:24 }}>
              <button onClick={handleAddSample}
                style={{ background:'none', border:'none', color:theme.textMuted, fontSize:13, cursor:'pointer', textDecoration:'underline' }}>
                または、サンプル案件を追加して試す
              </button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign:'center', padding:'40px 0', color:theme.textMuted, fontSize:14, lineHeight:1.7 }}>
            該当する案件がありません<br/>
            <span style={{ fontSize:13 }}>フィルターを変更してみてください</span>
          </div>
        )
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(p => {
            const sc = STAGE_COLOR[p.status] || STAGE_COLOR['提案中'];
            const stageIdx = STAGES.indexOf(p.status);
            const progress = (stageIdx / (STAGES.length-1)) * 100;
            const isUninvoiced = p.status !== '請求済み' && p.status !== 'invoiced' && (p.amount || 0) > 0;
            return (
              <div key={p.id} style={s.card}>
                <div style={{ height:3, background:theme.badgeBg1 }}>
                  <div style={{ height:'100%', width:`${progress}%`, background:sc.bar, transition:'width 0.3s' }} />
                </div>
                <div style={{ padding:'14px 16px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:15, fontWeight:600, color:theme.textPrimary, marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.title}</div>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        {p.clients?.name && (
                          <span style={{ fontSize:11, background:theme.badgeBg7, color:theme.badgeTxt7, padding:'2px 8px', borderRadius:20 }}><IconClients size={12}/> {p.clients.name}</span>
                        )}
                        {(() => {
                          const wt = WORK_TYPES.find(w => w.value === (p.work_type || 'project'));
                          return wt ? <span style={{ fontSize:11, background:wt.color.bg, color:wt.color.text, padding:'2px 8px', borderRadius:20 }}>{wt.label}</span> : null;
                        })()}
                        {p.tag && <span style={{ fontSize:11, background:theme.badgeBg9, color:theme.badgeTxt9, padding:'2px 8px', borderRadius:20 }}>{p.tag}</span>}
                        {isUninvoiced && <span style={{ fontSize:11, background:theme.badgeBg4, color:theme.badgeTxt4, padding:'2px 8px', borderRadius:20, fontWeight:600 }}><IconStar size={14}/> 未請求</span>}
                      </div>
                    </div>
                    <span style={{ flexShrink:0, marginLeft:10, fontSize:12, padding:'3px 10px', borderRadius:20, background:sc.badge.bg, color:sc.badge.color, fontWeight:500 }}>
                      <span style={{ display:'inline-block', width:6, height:6, borderRadius:'50%', background:sc.dot, marginRight:5, verticalAlign:'middle' }} />
                      {p.status}
                    </span>
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
                    {p.work_type === 'daily' ? (<>
                      <div style={{ background:theme.bgInput, borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontSize:10, color:theme.textMuted, marginBottom:2 }}>日当 × 稼働日数</div>
                        <div style={{ fontSize:13, fontWeight:600, color:theme.textPrimary }}>
                          {p.hourly_rate ? '¥'+Number(p.hourly_rate).toLocaleString('ja-JP') : '未設定'}
                          <span style={{ color:theme.textMuted, fontSize:11 }}> × {(p.project_work_dates||[]).length}日</span>
                        </div>
                      </div>
                      <div style={{ background:theme.bgInput, borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontSize:10, color:theme.textMuted, marginBottom:2 }}>合計見込み</div>
                        <div style={{ fontSize:13, fontWeight:600, color:theme.badgeTxt4 }}>
                          {p.hourly_rate && (p.project_work_dates||[]).length > 0
                            ? '¥'+Number(p.hourly_rate*(p.project_work_dates||[]).length).toLocaleString('ja-JP')
                            : '未設定'}
                        </div>
                      </div>
                    </>) : p.work_type === 'hourly' ? (<>
                      <div style={{ background:theme.bgInput, borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontSize:10, color:theme.textMuted, marginBottom:2 }}>時給 × 時間×日数</div>
                        <div style={{ fontSize:13, fontWeight:600, color:theme.textPrimary }}>
                          {p.hourly_rate ? '¥'+Number(p.hourly_rate).toLocaleString('ja-JP') : '未設定'}
                        </div>
                      </div>
                      <div style={{ background:theme.bgInput, borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontSize:10, color:theme.textMuted, marginBottom:2 }}>合計見込み</div>
                        <div style={{ fontSize:13, fontWeight:600, color:theme.badgeTxt2 }}>
                          {p.hourly_rate && p.hours_worked && p.work_days
                            ? '¥'+Number(p.hourly_rate*p.hours_worked*p.work_days).toLocaleString('ja-JP')
                            : formatCurrency(p.amount)}
                        </div>
                      </div>
                    </>) : (<>
                      <div style={{ background:theme.bgInput, borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontSize:10, color:theme.textMuted, marginBottom:2 }}>{p.work_type === 'monthly' ? '月額' : '金額'}</div>
                        <div style={{ fontSize:13, fontWeight:600, color:theme.textPrimary }}>{formatCurrency(p.amount)}</div>
                      </div>
                      <div style={{ background:theme.bgInput, borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontSize:10, color:theme.textMuted, marginBottom:2 }}>日付</div>
                        <div style={{ fontSize:13, fontWeight:600, color:theme.textPrimary }}>{formatDate(p.due_date)}</div>
                      </div>
                    </>)}
                  </div>

                  {p.memo && <p style={{ fontSize:12, color:theme.textMuted, margin:'0 0 12px', lineHeight:1.5, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{p.memo}</p>}

                  {/* 経費サマリー */}
                  {projectExpenses[p.id]?.length > 0 && (() => {
                    const exps = projectExpenses[p.id];
                    const total = exps.reduce((s,e) => s+(e.amount||0), 0);
                    return (
                      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
                        <span style={{ fontSize:11, background:theme.expenseBg, color:theme.expenseTxt, padding:'3px 10px', borderRadius:20, border:`1px solid ${theme.expenseBorder}` }}>
                          <IconExpenses size={12}/> 経費 ¥{total.toLocaleString('ja-JP')}（{exps.length}件）
                        </span>
                      </div>
                    );
                  })()}

                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {stageIdx < STAGES.length-1 && (
                      <button onClick={() => advanceStage(p)} style={{ flex:1, background:theme.badgeBg2, color:theme.badgeTxt2, border:'none', borderRadius:8, padding:'8px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        → {STAGES[stageIdx+1]}へ
                      </button>
                    )}
                    <button onClick={() => openEdit(p)} style={{ flex:1, background:theme.accentBg, color:theme.accent, border:`1px solid ${theme.accent}`, borderRadius:10, padding:'11px', fontSize:13, fontWeight:600, cursor:'pointer' }}><IconEdit size={14}/> 編集</button>
                    <button onClick={() => handleDuplicate(p)} style={{ background:theme.badgeBg2, color:theme.badgeTxt7, border:'none', borderRadius:8, padding:'8px 12px', fontSize:12, fontWeight:500, cursor:'pointer' }}>複製</button>
                    <button onClick={() => openExpense(p)} style={{ background:theme.expenseBg, color:theme.expenseTxt, border:`1px solid ${theme.expenseBorder}`, borderRadius:8, padding:'8px 12px', fontSize:12, fontWeight:500, cursor:'pointer' }}><IconExpenses size={14}/> 経費</button>
                    {/* 公開/非公開 機能：一旦非表示（後で戻すときは下の「false &&」を外す） */}
                    {false && hasAdmins && (
                      <button onClick={() => setVisibilityTarget({ id: p.id, title: p.title })}
                        style={{
                          background: (visibilityMap[p.id]?.length > 0) ? '#1e1b4b' : theme.bgInput,
                          color: (visibilityMap[p.id]?.length > 0) ? '#a5b4fc' : theme.textMuted,
                          border: `1px solid ${(visibilityMap[p.id]?.length > 0) ? theme.accent : theme.border}`,
                          borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer',
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1,
                        }}>
                        {visibilityMap[p.id]?.length > 0 ? (
                          <>
                            <span style={{ fontWeight: 700 }}>公開中({visibilityMap[p.id].length})</span>
                            {/* 公開中の管理者名を1件目だけ表示 */}
                            {(() => {
                              const afId = visibilityMap[p.id][0];
                              const info = adminInfoMap[afId];
                              if (!info) return null;
                              const label = [info.company_name, info.name].filter(Boolean).join(' ');
                              return <span style={{ fontSize: 10, color: '#818cf8', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>;
                            })()}
                          </>
                        ) : '非公開'}
                      </button>
                    )}
                    {/* 管理者から依頼された案件：取り下げ依頼ボタン */}
                    {(() => {
                      const matched = p.request_id ? requestMap[p.request_id] : null;
                      if (!matched) return null;
                      const isRequested = matched.status === 'withdraw_requested';
                      return (
                        <button
                          onClick={() => !isRequested && setWithdrawTarget(matched)}
                          style={{
                            background: isRequested ? 'rgba(234,179,8,0.1)' : 'rgba(239,68,68,0.08)',
                            color: isRequested ? '#fbbf24' : '#f87171',
                            border: `1px solid ${isRequested ? 'rgba(234,179,8,0.3)' : 'rgba(239,68,68,0.3)'}`,
                            borderRadius: 8, padding: '8px 12px', fontSize: 12,
                            cursor: isRequested ? 'default' : 'pointer', fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {isRequested ? '⏳ 取り下げ申請中' : '↩ 取り下げ依頼'}
                        </button>
                      );
                    })()}
                    <button onClick={() => handleDelete(p.id)} style={{ background:theme.dangerBg, color:theme.dangerText, border:'none', borderRadius:8, padding:'8px 12px', fontSize:12, fontWeight:500, cursor:'pointer' }}>削除</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div onClick={e => { if(e.target===e.currentTarget) setShowModal(false); }}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:50, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div style={{ background:theme.bgCard, width:'100%', maxWidth:520, borderRadius:'20px 20px 0 0', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ position:'sticky', top:0, background:theme.bgCard, borderBottom:`1px solid ${theme.border}`, padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'20px 20px 0 0' }}>
              <h2 style={{ fontSize:16, fontWeight:700, color:theme.textPrimary, margin:0 }}>{editProject ? '案件を編集' : '新規案件を追加'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background:'none', border:'none', color:theme.textMuted, fontSize:22, cursor:'pointer', lineHeight:1 }}>✕</button>
            </div>
            <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:16 }}>

              {/* ★ クライアント選択 */}
              <div>
                <div style={s.label}>クライアント</div>
                <select value={form.client_id} onChange={e => setForm({...form, client_id:e.target.value})} style={s.input}>
                  <option value=''>未設定</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {clients.length === 0 && (
                  <div style={{ fontSize:11, color:theme.textMuted, marginTop:4 }}>※ クライアント管理ページで先に登録できます</div>
                )}
              </div>

              {/* 案件名 */}
              <div>
                <div style={s.label}>案件名 *</div>
                <input type="text" placeholder="例：ECサイトLP制作" value={form.title}
                  onChange={e => setForm({...form, title:e.target.value})} style={s.input} />
              </div>

              {/* タグ */}
              <div>
                <div style={s.label}>タグ</div>
                <input type="text" placeholder="例：デザイン、開発" value={form.tag}
                  onChange={e => setForm({...form, tag:e.target.value})} style={s.input} />
              </div>

              {/* 仕事タイプ */}
              <div>
                <div style={s.label}>仕事タイプ</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  {WORK_TYPES.map(wt => (
                    <button key={wt.value} onClick={() => setForm({...form, work_type:wt.value})}
                      style={{ padding:'10px 8px', borderRadius:10, border:'none', fontSize:13, fontWeight:600, cursor:'pointer',
                        background: form.work_type === wt.value ? wt.color.bg : theme.bgInput,
                        color: form.work_type === wt.value ? wt.color.text : theme.textMuted,
                        outline: form.work_type === wt.value ? `1px solid ${wt.color.text}` : `1px solid ${theme.border}` }}>
                      {wt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* タイプ別入力 */}
              {form.work_type === 'project' && (<>
                <div>
                  <div style={s.label}>金額（円）</div>
                  <input type="number" placeholder="100000" value={form.amount}
                    onChange={e => setForm({...form, amount:e.target.value})} style={s.input} />
                  {form.amount && <div style={{ fontSize:11, color:theme.textMuted, marginTop:4 }}>{'¥'+Number(form.amount).toLocaleString('ja-JP')}</div>}
                </div>
                <div>
                  <div style={s.label}>開始日</div>
                  <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date:e.target.value})} style={s.input} />
                </div>
                <div>
                  <div style={s.label}>納期・終了日</div>
                  <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date:e.target.value})} style={s.input} />
                </div>
              </>)}

              {form.work_type === 'hourly' && (<>
                <div>
                  <div style={s.label}>時給（円）</div>
                  <input type="number" placeholder="3000" value={form.hourly_rate}
                    onChange={e => setForm({...form, hourly_rate:e.target.value})} style={s.input} />
                </div>
                <div>
                  <div style={s.label}>1日あたりの稼働時間（h）</div>
                  <input type="number" placeholder="8" value={form.hours_worked}
                    onChange={e => setForm({...form, hours_worked:e.target.value})} style={s.input} />
                </div>
                <div>
                  <div style={{ ...s.label, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span><IconCalendar size={14}/> 稼働日を選択 <span style={{ color:theme.badgeTxt2, fontWeight:700 }}>{workDates.length}日</span></span>
                    {workDates.length > 0 && (
                      <button onClick={() => setWorkDates([])}
                        style={{ fontSize:10, color:theme.colorRed, background:'none', border:'none', cursor:'pointer' }}>クリア</button>
                    )}
                  </div>
                  <WorkDatePicker workDates={workDates} setWorkDates={setWorkDates} />
                  {form.hourly_rate && form.hours_worked && workDates.length > 0 && (
                    <div style={{ fontSize:11, color:theme.badgeTxt2, marginTop:4 }}>
                      {'合計見込み：¥'+Number(parseInt(form.hourly_rate)*parseFloat(form.hours_worked)*workDates.length).toLocaleString('ja-JP')+' （'+workDates.length+'日）'}
                    </div>
                  )}
                </div>
              </>)}

              {form.work_type === 'daily' && (<>
                <div>
                  <div style={s.label}>日当（円）</div>
                  <input type="number" placeholder="30000" value={form.hourly_rate}
                    onChange={e => setForm({...form, hourly_rate:e.target.value})} style={s.input} />
                  {form.hourly_rate && workDates.length > 0 && (
                    <div style={{ fontSize:11, color:theme.badgeTxt4, marginTop:4 }}>
                      {'合計見込み：¥'+Number(parseInt(form.hourly_rate)*workDates.length).toLocaleString('ja-JP')+' （'+workDates.length+'日）'}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ ...s.label, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span><IconCalendar size={14}/> 稼働日を選択 <span style={{ color:theme.badgeTxt4, fontWeight:700 }}>{workDates.length}日</span></span>
                    {workDates.length > 0 && (
                      <button onClick={() => setWorkDates([])}
                        style={{ fontSize:10, color:theme.colorRed, background:'none', border:'none', cursor:'pointer' }}>クリア</button>
                    )}
                  </div>
                  <WorkDatePicker workDates={workDates} setWorkDates={setWorkDates} />
                </div>
              </>)}

              {form.work_type === 'monthly' && (<>
                <div>
                  <div style={s.label}>月額（円）</div>
                  <input type="number" placeholder="300000" value={form.amount}
                    onChange={e => setForm({...form, amount:e.target.value})} style={s.input} />
                  {form.amount && <div style={{ fontSize:11, color:theme.textMuted, marginTop:4 }}>{'¥'+Number(form.amount).toLocaleString('ja-JP')}</div>}
                </div>
                <div>
                  <div style={s.label}>開始日</div>
                  <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date:e.target.value})} style={s.input} />
                </div>
                <div>
                  <div style={s.label}>終了日</div>
                  <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date:e.target.value})} style={s.input} />
                </div>
              </>)}

              <div>
                <div style={s.label}>支払い方法</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {['現金','銀行振込','先払い','後払い','その他'].map(m => (
                    <button key={m} onClick={() => setForm({...form, payment_method:m})}
                      style={{ padding:'6px 12px', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer',
                        background: form.payment_method === m ? theme.accentBg : theme.bgCard,
                        color: form.payment_method === m ? theme.colorBlue : theme.textMuted,
                        outline: form.payment_method === m ? '1px solid #3b82f6' : `1px solid ${theme.border}` }}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div style={s.label}>ステータス</div>
                <select value={form.status} onChange={e => setForm({...form, status:e.target.value})} style={s.input}>
                  {STAGES.map(st => <option key={st}>{st}</option>)}
                </select>
              </div>
              <div>
                <div style={s.label}>メモ</div>
                <textarea rows={3} placeholder="案件の詳細や備考..." value={form.memo}
                  onChange={e => setForm({...form, memo:e.target.value})}
                  style={{ ...s.input, resize:'vertical' }} />
              </div>
              <div style={{ display:'flex', gap:10, paddingBottom:8 }}>
                <button onClick={() => setShowModal(false)} style={{ flex:1, background:theme.badgeBg1, color:theme.textSecondary, border:'none', borderRadius:10, padding:'12px', fontSize:14, cursor:'pointer' }}>キャンセル</button>
                <button onClick={handleSave} disabled={!form.title.trim()}
                  style={{ flex:1, background:form.title.trim()?theme.primary:theme.bgInput, color:form.title.trim()?theme.primaryText:theme.textMuted, border:'none', borderRadius:10, padding:'12px', fontSize:14, fontWeight:600, cursor:form.title.trim()?'pointer':'default' }}>
                  {editProject ? '更新する' : '追加する'}
                </button>
              </div>

              {/* ── AI見積もりセクション ── */}
              <div style={{ borderTop:`1px solid ${theme.border}`, paddingTop:16 }}>
                <button onClick={() => setShowEstimate(v => !v)}
                  style={{ width:'100%', background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:'10px 16px', fontSize:13, fontWeight:600, color:theme.colorPurple, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span><IconAi size={14}/> AIで見積もりを提案してもらう</span>
                  <span style={{ fontSize:12, color:theme.textMuted }}>{showEstimate ? '▲ 閉じる' : '▼ 開く'}</span>
                </button>

                {showEstimate && (
                  <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:12 }}>
                    {[
                      { label:'概要・作業内容', key:'description', placeholder:'例：Reactでのフロントエンド開発、APIはFastAPI', type:'textarea' },
                      { label:'工数目安', key:'workload', placeholder:'例：40時間、2週間', type:'text' },
                      { label:'役割', key:'role', placeholder:'例：フロントエンド開発、デザイン＋実装', type:'text' },
                      { label:'希望単価レンジ', key:'price_range', placeholder:'例：時給5,000〜8,000円', type:'text' },
                    ].map(f => (
                      <div key={f.key}>
                        <div style={{ fontSize:11, color:theme.textSecondary, marginBottom:4 }}>{f.label}</div>
                        {f.type === 'textarea' ? (
                          <textarea rows={2} placeholder={f.placeholder} value={estimateForm[f.key]}
                            onChange={e => setEstimateForm(ef => ({...ef, [f.key]: e.target.value}))}
                            style={{ width:'100%', background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:'10px 14px', fontSize:13, color:theme.textPrimary, outline:'none', boxSizing:'border-box', resize:'vertical' }} />
                        ) : (
                          <input type="text" placeholder={f.placeholder} value={estimateForm[f.key]}
                            onChange={e => setEstimateForm(ef => ({...ef, [f.key]: e.target.value}))}
                            style={{ width:'100%', background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:'10px 14px', fontSize:13, color:theme.textPrimary, outline:'none', boxSizing:'border-box' }} />
                        )}
                      </div>
                    ))}

                    <button onClick={handleEstimate} disabled={estimateLoading}
                      style={{ width:'100%', background: estimateLoading ? theme.bgInput : `linear-gradient(135deg, ${theme.accent}, ${theme.accentSub})`, color: estimateLoading ? theme.textMuted : '#fff', border:'none', borderRadius:10, padding:'12px', fontSize:14, fontWeight:600, cursor: estimateLoading ? 'default' : 'pointer' }}>
                      {estimateLoading ? 'AIが考え中...' : '見積もりを生成'}
                    </button>

                    {estimateError && (
                      <div style={{ background:theme.dangerBg, border:'1px solid #7f1d1d', borderRadius:10, padding:'10px 14px', fontSize:13, color:theme.dangerText }}>{estimateError}</div>
                    )}

                    {estimate && (
                      <div style={{ background:theme.bgInput, border:`1px solid ${theme.accent}`, borderRadius:12, padding:16, display:'flex', flexDirection:'column', gap:12 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <span style={{ fontSize:11, color:theme.colorPurple, fontWeight:700, letterSpacing:'0.05em' }}>AI見積もり結果</span>
                          <span style={{ fontSize:20, fontWeight:800, color:theme.textPrimary }}>¥{estimate.suggested_price_yen.toLocaleString('ja-JP')}</span>
                        </div>

                        {/* 内訳 */}
                        <div>
                          <div style={{ fontSize:11, color:theme.textMuted, marginBottom:6 }}>内訳</div>
                          {estimate.breakdown.map((b, i) => (
                            <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'4px 0', borderBottom:'1px solid #1e293b' }}>
                              <span style={{ color:theme.colorLight }}>{b.label}</span>
                              <span style={{ color:theme.textPrimary, fontWeight:600 }}>¥{b.yen.toLocaleString('ja-JP')}</span>
                            </div>
                          ))}
                        </div>

                        {/* 前提条件 */}
                        <div>
                          <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4 }}>前提条件</div>
                          {estimate.assumptions.map((a, i) => (
                            <div key={i} style={{ fontSize:12, color:theme.textSecondary, paddingLeft:8, marginBottom:2 }}>• {a}</div>
                          ))}
                        </div>

                        {/* 確認事項 */}
                        <div>
                          <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4 }}>クライアントへの確認事項</div>
                          {estimate.questions.map((q, i) => (
                            <div key={i} style={{ fontSize:12, color:theme.badgeTxt3, paddingLeft:8, marginBottom:2 }}>? {q}</div>
                          ))}
                        </div>

                        <button onClick={applyEstimate}
                          style={{ width:'100%', background:theme.badgeBg6, color:theme.badgeTxt6, border:'1px solid #166534', borderRadius:10, padding:'10px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                          ✅ 見積金額を案件に反映（¥{estimate.suggested_price_yen.toLocaleString('ja-JP')}）
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 公開設定モーダル */}
      {visibilityTarget && (
        <VisibilityModal
          projectId={visibilityTarget.id}
          projectTitle={visibilityTarget.title}
          onClose={() => { setVisibilityTarget(null); fetchVisibilityData(); }}
        />
      )}

      {/* 経費追加モーダル */}
      {showExpenseModal && (
        <div onClick={() => setShowExpenseModal(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:1000, padding:'0' }}>
          <div onClick={e => e.stopPropagation()} style={{ background:theme.bgCard, width:'100%', maxWidth:520, borderRadius:'20px 20px 0 0', padding:24, paddingBottom:40 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:theme.textPrimary }}><IconExpenses size={16}/> 経費追加</div>
                <div style={{ fontSize:12, color:theme.badgeTxt7, marginTop:2 }}>📋 {expenseForm.project_title}</div>
              </div>
              <button onClick={() => setShowExpenseModal(false)} style={{ background:'none', border:'none', color:theme.textMuted, fontSize:20, cursor:'pointer' }}>✕</button>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <div style={s.label}>日付</div>
                <input type="date" value={expenseForm.date} onChange={e => setExpenseForm({...expenseForm, date:e.target.value})} style={s.input} />
              </div>
              <div>
                <div style={s.label}>金額（円） *</div>
                <input type="number" placeholder="例：3000" value={expenseForm.amount} onChange={e => setExpenseForm({...expenseForm, amount:e.target.value})} style={s.input} />
              </div>
              {/* 消費税 */}
              <div>
                <div style={s.label}>消費税</div>
                <div style={{ display:'flex', gap:8 }}>
                  {[{ label:'税抜き（別途10%）', val:false }, { label:'税込み（消費税込）', val:true }].map(opt => (
                    <button key={String(opt.val)} onClick={() => setExpenseTaxIncluded(opt.val)}
                      style={{ flex:1, padding:'8px', borderRadius:10, border:'none', fontSize:12, fontWeight:600, cursor:'pointer',
                        background: expenseTaxIncluded === opt.val ? (opt.val ? theme.badgeBg6 : theme.badgeBg2) : theme.bgInput,
                        color: expenseTaxIncluded === opt.val ? (opt.val ? theme.badgeTxt6 : theme.badgeTxt2) : theme.textMuted,
                        outline: expenseTaxIncluded === opt.val ? 'none' : `1px solid ${theme.border}`,
                      }}>{opt.label}</button>
                  ))}
                </div>
                {expenseForm.amount && !expenseTaxIncluded && (
                  <div style={{ fontSize:11, color:'#93c5fd', marginTop:4 }}>税込換算: ¥{Math.floor(Number(expenseForm.amount)*1.1).toLocaleString('ja-JP')}</div>
                )}
              </div>
              <div>
                <div style={s.label}>カテゴリ</div>
                <select value={expenseForm.category} onChange={e => setExpenseForm({...expenseForm, category:e.target.value})} style={s.input}>
                  {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={s.label}>メモ</div>
                <input type="text" placeholder="例：クライアント先への交通費" value={expenseForm.memo} onChange={e => setExpenseForm({...expenseForm, memo:e.target.value})} style={s.input} />
              </div>
              {/* レシート画像 */}
              <div>
                <div style={{ ...s.label, marginBottom:8 }}>📷 レシート画像（任意）</div>
                <label style={{ display:'block', cursor:'pointer' }}>
                  <input type="file" accept="image/*" style={{ display:'none' }}
                    onChange={ev => {
                      const file = ev.target.files?.[0];
                      if (!file) return;
                      setExpenseReceiptFile(file);
                      const reader = new FileReader();
                      reader.onload = e2 => setExpenseReceiptPreview(e2.target.result);
                      reader.readAsDataURL(file);
                    }} />
                  {expenseReceiptPreview ? (
                    <div style={{ position:'relative' }}>
                      <img src={expenseReceiptPreview} alt="レシート"
                        style={{ width:'100%', maxHeight:180, objectFit:'contain', borderRadius:10, border:`1px solid ${theme.border}`, background:theme.bgInput, display:'block' }} />
                      <button onClick={ev => { ev.preventDefault(); setExpenseReceiptFile(null); setExpenseReceiptPreview(null); }}
                        style={{ position:'absolute', top:6, right:6, background:'rgba(0,0,0,0.7)', border:'none', borderRadius:'50%', width:26, height:26, color:'#fff', fontSize:15, cursor:'pointer', lineHeight:'26px', textAlign:'center', padding:0 }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ border:`2px dashed ${theme.border}`, borderRadius:10, padding:'14px', textAlign:'center', color:theme.textMuted, fontSize:13 }}>
                      📷 タップして画像を選択
                    </div>
                  )}
                </label>
              </div>
              <button onClick={saveExpense} disabled={expenseSaving || !expenseForm.amount} style={{ width:'100%', padding:'13px', borderRadius:12, border:'none', fontSize:15, fontWeight:600, cursor: expenseSaving || !expenseForm.amount ? 'default' : 'pointer', background: expenseSaving || !expenseForm.amount ? theme.badgeBg1 : theme.expenseBg, color: expenseSaving || !expenseForm.amount ? theme.textMuted : theme.expenseTxt }}>
                {expenseSaving ? '保存中...' : '経費を追加する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== 取り下げ依頼 確認モーダル ===== */}
      {withdrawTarget && (
        <div
          onClick={() => setWithdrawTarget(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:16, padding:'24px 20px', width:'100%', maxWidth:400 }}
          >
            <div style={{ fontSize:17, fontWeight:700, color:theme.textPrimary, marginBottom:10 }}>取り下げ依頼を送りますか？</div>
            <p style={{ fontSize:13, color:theme.textSecondary, lineHeight:1.7, marginBottom:22 }}>
              案件 <strong style={{ color:theme.textPrimary }}>「{withdrawTarget.title}」</strong> の取り下げを管理者に依頼します。<br/>
              管理者が承認すると、この案件は削除されます。
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <button
                onClick={() => setWithdrawTarget(null)}
                style={{ flex:1, background:'transparent', color:theme.textSecondary, border:`1px solid ${theme.border}`, borderRadius:10, padding:'11px', fontSize:14, cursor:'pointer' }}
              >キャンセル</button>
              <button
                onClick={handleWithdrawRequest}
                disabled={withdrawing}
                style={{ flex:1, background:'rgba(239,68,68,0.15)', color:'#f87171', border:'1px solid rgba(239,68,68,0.35)', borderRadius:10, padding:'11px', fontSize:14, fontWeight:700, cursor: withdrawing ? 'default' : 'pointer', opacity: withdrawing ? 0.6 : 1 }}
              >{withdrawing ? '送信中...' : '↩ 取り下げ依頼を送る'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
